import React from 'react';
import {createGuid} from "app/utilities/StringHelpers/StringHelpers";
import EpiContext from '@episerver/spa-core/Spa';
import {
  DISNEY_REGISTRATION_PARAMS,
  HBO_REGISTRATION_PARAMS,
  StreamingRegistrationStatus,
  StreamingRegistrationFlowSteps,
  StreamingRegistrationProcessTypes,
} from './constants';
import {
  getSubscriberRegistrationStatus,
  getJsonParametersBySubscriber,
  getTokenBeforeRedirect,
  getDisneyPlusTokenBeforeRedirect,
  setSelectedTvSubscriber,
  setJsonParameters,
  setRegistrationParams,
} from '../StreamingRegistrationSlice';
import Config from 'app/Config';
import {getTvSubscribersForRegistration} from 'app/Components/Block/PremiumPackagesCatalogBlock/premiumPackagesCatalogAsyncThunk';
import LoadingScreen from '../components/CheckRegistrationStatusLoader';
import WhiteBtnAssistancePage from '../components/WhiteBtnAssistancePage';
import {navigateToUrl, buildUrlAndOpenSameWindowWithParams} from 'app/utilities/StringHelpers/StringHelpers';
import errorIcon from '../assets/images/errorIcon.svg';

const getRegistrationSliceState = () =>
  EpiContext.getStore().getState().streamingRegistrationSlice || {};

export const getInitialStep = () => StreamingRegistrationFlowSteps.CHOOSE_FLOW;
export const getInitialProcessDefinition = () => StreamingRegistrationProcessTypes.ML;
export const getInitialScreen = () => LoadingScreen;

const normalizeRedirectPath = (url) => {
  if (!url) return '';
  const withoutQuery = url.split('?')[0].split('#')[0];
  if (withoutQuery.startsWith('http')) {
    try {
      return new URL(withoutQuery).pathname.toLowerCase().replace(/\/$/, '');
    } catch {
      return withoutQuery.toLowerCase().replace(/\/$/, '');
    }
  }
  return withoutQuery.toLowerCase().replace(/\/$/, '');
};

const getCurrentPagePath = () => {
  const path = window.location.pathname.toLowerCase().replace(/\/$/, '');
  const hashPath = window.location.hash.split('?')[0].toLowerCase().replace(/\/$/, '');
  return hashPath && hashPath !== '#' ? hashPath : path;
};

export const syncRegistrationParamsFromRedirectPage = () => {
  const currentPath = getCurrentPagePath();
  const disneyPath = normalizeRedirectPath(Config.redirectToDisneyUrl);
  const hboPath = normalizeRedirectPath(Config.redirectToHboUrl);

  if (disneyPath && (currentPath === disneyPath || currentPath.endsWith(disneyPath))) {
    EpiContext.getStore().dispatch(setRegistrationParams(DISNEY_REGISTRATION_PARAMS));
    return;
  }
  if (hboPath && (currentPath === hboPath || currentPath.endsWith(hboPath))) {
    EpiContext.getStore().dispatch(setRegistrationParams(HBO_REGISTRATION_PARAMS));
  }
};

const buildDisneyConnectJsonParameters = (sta1, entitlementId) =>
  JSON.stringify({
    provider: 'CELLCOM_IL',
    entitlements: {
      [entitlementId]: {
        products: sta1,
      },
    },
    address: {
      country: 'IL',
    },
  });

const resolveDisneyConnectJsonParameters = (apiJsonParameters, entitlementId) => {
  if (!apiJsonParameters || !entitlementId) {
    return null;
  }

  const rawValue = typeof apiJsonParameters === 'string' ? apiJsonParameters.trim() : apiJsonParameters;

  if (typeof rawValue === 'string' && rawValue.startsWith('{')) {
    try {
      const parsed = JSON.parse(rawValue);
      if (parsed?.provider === 'CELLCOM_IL' && parsed?.entitlements) {
        return rawValue;
      }
    } catch {
      // treat as STA1 product string
    }
  }

  if (typeof rawValue === 'object' && rawValue?.provider === 'CELLCOM_IL' && rawValue?.entitlements) {
    return JSON.stringify(rawValue);
  }

  const sta1 = typeof rawValue === 'string' ? rawValue : String(rawValue);
  return buildDisneyConnectJsonParameters(sta1, entitlementId);
};

const createHboTokenToSessionParamObject = (jsonParameters) => {
  const store = EpiContext.getStore().getState();
  const {connectClientName} = getRegistrationSliceState();
  const {phoneNumber, idNumber} = store.authentication?.loginData?.magicLinkParameters || {};
  const {productSubscriberNo} = store.selfcarePage?.selectedSubscriber || {};
  const {IDNumber: idNumberFromAuth} = store.authentication?.loginData?.userDetails || {};

  return {
    connectClientName,
    subscriberNumber: phoneNumber ?? productSubscriberNo,
    idNumber: idNumber ?? idNumberFromAuth,
    jsonParameters,
    hash: createGuid(),
    sid: createGuid(),
  };
};

const createDisneyTokenToSessionParamObject = (apiJsonParameters) => {
  const store = EpiContext.getStore().getState();
  const {connectClientName, subscriberExternalId, selectedTvSubscriber} = getRegistrationSliceState();
  const {subscriberNo} = store.premiumPackagesCatalog?.selectedSubscriber || {};
  const entitlementId = selectedTvSubscriber ?? subscriberNo;
  const {phoneNumber, idNumber} = store.authentication?.loginData?.magicLinkParameters || {};
  const {productSubscriberNo} = store.selfcarePage?.selectedSubscriber || {};
  const {IDNumber: idNumberFromAuth} = store.authentication?.loginData?.userDetails || {};

  const jsonParameters = resolveDisneyConnectJsonParameters(apiJsonParameters, entitlementId);
  if (!jsonParameters || !subscriberExternalId) {
    return null;
  }

  return {
    connectClientName,
    subscriberNumber: phoneNumber ?? productSubscriberNo,
    idNumber: idNumber ?? idNumberFromAuth,
    sub: subscriberExternalId,
    jsonParameters,
    hash: createGuid(),
    sid: createGuid(),
  };
};

const fetchJsonParametersBySubscriber = async () => {
  const store = EpiContext.getStore().getState();
  const {jsonParameters, selectedTvSubscriber} = getRegistrationSliceState();
  const {subscriberNo} = store.premiumPackagesCatalog?.selectedSubscriber || {};
  const tvSubscriber = selectedTvSubscriber ?? subscriberNo;

  if (!tvSubscriber) {
    return {jsonParameters: jsonParameters || null, status: null, error: false};
  }

  const result = await EpiContext.getStore().dispatch(
    getJsonParametersBySubscriber({tvSubscriber}),
  );
  const {error, data} = result.payload || {};
  if (error) {
    return {jsonParameters: null, status: null, error: true};
  }

  const {jsonParameters: apiJsonParameters, status} = data || {};
  return {
    jsonParameters: apiJsonParameters || jsonParameters || null,
    status,
    error: false,
  };
};

const effectiveMlProcessStatus = (status) => {
  switch(status) {
    case StreamingRegistrationStatus.CHA2:{
      return StreamingRegistrationFlowSteps.GET_TOKEN;
      break;
    }
    case StreamingRegistrationStatus.CHA3:{
      return StreamingRegistrationFlowSteps.REDIRECT_TO_WORLD;
      break;
    }
    case StreamingRegistrationStatus.ReturningCustomer:{
      return StreamingRegistrationFlowSteps.GET_TOKEN;
      break;
    }
    case StreamingRegistrationStatus.SubscriberNotFound:
    case StreamingRegistrationStatus.StreamingChannelNotFound:
    case StreamingRegistrationStatus.CHA1:
    case StreamingRegistrationStatus.GeneralError:
    default: {
      return StreamingRegistrationFlowSteps.ERROR_SCREEN
      break;
    }
  }
};

const expiredMlProcessStatus = (status) => {
  switch(status) {
    case StreamingRegistrationStatus.CHA2:{
      return StreamingRegistrationFlowSteps.GET_TOKEN;
      break;
    }
    case StreamingRegistrationStatus.ReturningCustomer:{
      return StreamingRegistrationFlowSteps.GET_TOKEN;
      break;
    }
    case StreamingRegistrationStatus.CHA3:
    case StreamingRegistrationStatus.SubscriberNotFound:
    case StreamingRegistrationStatus.StreamingChannelNotFound:
    case StreamingRegistrationStatus.CHA1:
    case StreamingRegistrationStatus.GeneralError:
    default: {
      return StreamingRegistrationFlowSteps.REDIRECT_TO_WORLD;
      break;
    }
  }
};

const chooseFlowProcess = () => {
  const store = EpiContext.getStore().getState();
  const currentProcess = getRegistrationSliceState().currentProcess || StreamingRegistrationProcessTypes.ML;

  switch (currentProcess) {
    case StreamingRegistrationProcessTypes.ML: {
      const {tvSubscriber, jsonParameters} = store.authentication?.loginData?.magicLinkParameters || {};
      if (jsonParameters) {
        EpiContext.getStore().dispatch(setJsonParameters(jsonParameters));
      }
      if (tvSubscriber) {
        EpiContext.getStore().dispatch(setSelectedTvSubscriber(tvSubscriber));
        return StreamingRegistrationFlowSteps.EFFECTIVE_ML_GET_REGISTRATION_STATUS;
      }
      return StreamingRegistrationFlowSteps.CHECK_IF_TV_SERVICE_EXISTS;
    }
    case StreamingRegistrationProcessTypes.PACKAGE_EXIST: {
      return StreamingRegistrationFlowSteps.GET_TOKEN;
    }
    case StreamingRegistrationProcessTypes.PACKAGE_EXIST_ADD_CHANNEL: {
      return StreamingRegistrationFlowSteps.GET_TOKEN;
    }
    case StreamingRegistrationProcessTypes.PACKAGE_NOT_EXIST: {
      return StreamingRegistrationFlowSteps.GET_TOKEN;
    }
    default: {
      return StreamingRegistrationFlowSteps.ERROR_SCREEN;
    }
  }
};

export const createBoundFlowMap = (cmsData, SubscriberListPageWrapper) => {
  return {
    [StreamingRegistrationFlowSteps.CHOOSE_FLOW]: {
      component: LoadingScreen,
      name: StreamingRegistrationFlowSteps.CHOOSE_FLOW,
      executeAction: () => {
        return chooseFlowProcess();
      },
    },
    [StreamingRegistrationFlowSteps.EFFECTIVE_ML_GET_REGISTRATION_STATUS]: {
      name: StreamingRegistrationFlowSteps.EFFECTIVE_ML_GET_REGISTRATION_STATUS,
      executeAsyncAction: async () => {
        try {
          const {selectedTvSubscriber, jsonParameters} = getRegistrationSliceState();
          if (!selectedTvSubscriber) {
            return StreamingRegistrationFlowSteps.ERROR_SCREEN;
          }

          const result = await EpiContext.getStore().dispatch(
            getSubscriberRegistrationStatus({
              tvSubscriber: selectedTvSubscriber,
              shouldCreateJsonParameters: jsonParameters ? false : true,
            }),
          );
          const {error, data: {status} = {}} = result.payload || {};
          if (error || !status) {
            return StreamingRegistrationFlowSteps.ERROR_SCREEN;
          }
          return effectiveMlProcessStatus(status);
        } catch (error) {
          return StreamingRegistrationFlowSteps.ERROR_SCREEN;
        }
      },
    },
    [StreamingRegistrationFlowSteps.CHECK_IF_TV_SERVICE_EXISTS]: {
      name: StreamingRegistrationFlowSteps.CHECK_IF_TV_SERVICE_EXISTS,
      executeAsyncAction: async () => {
         try {
          const result = await EpiContext.getStore().dispatch(
            getTvSubscribersForRegistration(),
          );
          const {error, tvSubscriberList} = result.payload || {};
          if (error) {
            return StreamingRegistrationFlowSteps.ERROR_SCREEN;
          }
          if (tvSubscriberList.length === 0) {
            return StreamingRegistrationFlowSteps.REDIRECT_TO_NO_TV_SERVICE;
          }
          if (tvSubscriberList.length === 1) {
            const subSelected = tvSubscriberList[0].subscriberNo;
            EpiContext.getStore().dispatch(setSelectedTvSubscriber(subSelected));
            return StreamingRegistrationFlowSteps.EXPIRED_ML_GET_REGISTRATION_STATUS;
          }
          return StreamingRegistrationFlowSteps.CHOOSE_TV_SUBSCRIPTION;
        } catch (error) {
          return StreamingRegistrationFlowSteps.ERROR_SCREEN;
         }
      },
    },
    [StreamingRegistrationFlowSteps.CHOOSE_TV_SUBSCRIPTION]: {
      name: StreamingRegistrationFlowSteps.CHOOSE_TV_SUBSCRIPTION,
      component: SubscriberListPageWrapper,
      props: {
        isStreamingRedirect: true,
        isHboRedirect: true,
      },
      onSubmit: (args) => {
        const subSelected = args?.subscriberNo;
        if(subSelected) {
          EpiContext.getStore().dispatch(setSelectedTvSubscriber(subSelected));
          return StreamingRegistrationFlowSteps.EXPIRED_ML_GET_REGISTRATION_STATUS;
        }
        return StreamingRegistrationFlowSteps.ERROR_SCREEN;
      },
    },
    [StreamingRegistrationFlowSteps.EXPIRED_ML_GET_REGISTRATION_STATUS]: {
      component: LoadingScreen,
      name: StreamingRegistrationFlowSteps.EXPIRED_ML_GET_REGISTRATION_STATUS,
      executeAsyncAction: async () => {
        try {
          const {selectedTvSubscriber} = getRegistrationSliceState();
          if (!selectedTvSubscriber) {
            return StreamingRegistrationFlowSteps.ERROR_SCREEN;
          }
          const result = await EpiContext.getStore().dispatch(
            getSubscriberRegistrationStatus({
              tvSubscriber: selectedTvSubscriber ,
              shouldCreateJsonParameters: true,
            }),
          );
          const {error, data: {status} = {}} = result.payload || {};
          if (error || !status) {
            return StreamingRegistrationFlowSteps.ERROR_SCREEN;
          }

          return expiredMlProcessStatus(status);
        } catch (error) {
          return StreamingRegistrationFlowSteps.ERROR_SCREEN;
        }
      },
    },
    [StreamingRegistrationFlowSteps.GET_TOKEN]: {
      component: LoadingScreen,
      name: StreamingRegistrationFlowSteps.GET_TOKEN,
      executeAsyncAction: async () => {
        try {
          const {jsonParameters, status, error} = await fetchJsonParametersBySubscriber();
          if (status === StreamingRegistrationStatus.ReturningCustomer) {
            return StreamingRegistrationFlowSteps.REDIRECT_TO_STREAMING_LOGIN;
          }
          if (error || !jsonParameters) {
            return StreamingRegistrationFlowSteps.ERROR_SCREEN;
          }

          const {connectClientName} = getRegistrationSliceState();
          const isDisney = connectClientName === 'DISNEY';
          const paramObject = isDisney
            ? createDisneyTokenToSessionParamObject(jsonParameters)
            : createHboTokenToSessionParamObject(jsonParameters);
          if (!paramObject) {
            return StreamingRegistrationFlowSteps.ERROR_SCREEN;
          }
          const getTokenThunk = isDisney ? getDisneyPlusTokenBeforeRedirect : getTokenBeforeRedirect;
          const result = await EpiContext.getStore().dispatch(getTokenThunk(paramObject));

          const {error: tokenError, accessToken} = result.payload || {};
          if (tokenError || !accessToken) {
            return StreamingRegistrationFlowSteps.ERROR_SCREEN;
          }
          return StreamingRegistrationFlowSteps.REDIRECT_TO_OFFICIAL_PAGE;
        } catch (error) {
          return StreamingRegistrationFlowSteps.ERROR_SCREEN;
        }
      },
    },
    [StreamingRegistrationFlowSteps.REDIRECT_TO_WORLD]: {
      name: StreamingRegistrationFlowSteps.REDIRECT_TO_WORLD,
      executeAction: () => {
        const {worldUrlParameter} = getRegistrationSliceState();
        const {worldPageUrl} = cmsData;
        buildUrlAndOpenSameWindowWithParams(worldPageUrl?.value, worldUrlParameter);
      },
    },
    [StreamingRegistrationFlowSteps.REDIRECT_TO_OFFICIAL_PAGE]: {
      name: StreamingRegistrationFlowSteps.REDIRECT_TO_OFFICIAL_PAGE,
      executeAction: () => {
        const {token, connectClientName} = getRegistrationSliceState();
        if (!token) {
          return StreamingRegistrationFlowSteps.ERROR_SCREEN;
        }

        const {officialPageUrl} = cmsData;
        const tokenParam = connectClientName === 'DISNEY' ? 'token' : 'code';
        window.open(officialPageUrl?.value + '&' + tokenParam + '=' + token, '_self');
      },
    },
    [StreamingRegistrationFlowSteps.REDIRECT_TO_NO_TV_SERVICE]: {
      name: StreamingRegistrationFlowSteps.REDIRECT_TO_NO_TV_SERVICE,
      executeAction: () => {
        const {noTvServicePageUrl} = cmsData;
        navigateToUrl(noTvServicePageUrl?.value);
      },
    },
    [StreamingRegistrationFlowSteps.REDIRECT_TO_STREAMING_LOGIN]: {
      name: StreamingRegistrationFlowSteps.REDIRECT_TO_STREAMING_LOGIN,
      executeAction: () => {
        const {streamingServiceLoginUrl} = cmsData;
        navigateToUrl(streamingServiceLoginUrl?.value);
      },
    },
    [StreamingRegistrationFlowSteps.ERROR_SCREEN]: {
      name: StreamingRegistrationFlowSteps.ERROR_SCREEN,
      component: WhiteBtnAssistancePage,
      props: {
        icon: errorIcon,
        title: <>מצטערים, משהו השתבש ולא <br/>הצלחנו למצוא את השירות המבוקש</>,
        assistanceText: 'נשמח לסייע לך',
        assistanceBtnText: 'ב-*'+ window.Config.technicalSupportPhone,
        onAssistanceBtnClick: () => {
          window.open('tel:*'+ window.Config.technicalSupportPhone);
        },
        assistanceBtnTextDesktop: 'תתקשרו אלי',
        assistanceUnderBtnTextDesktop: 'חייגו *'+ window.Config.technicalSupportPhone + ' לנציג שירות',
        footerBtnText: 'חזרה לאזור האישי',
        onFooterBtnClick: () => {
          navigateToUrl();
        }
      }
    },
  };
};
